import React from "react"
// @ts-ignore
import LazyLoad from "react-lazyload"

import { useLocation } from "react-router-dom"
import { Location } from "history"
import { LICENSING_STEP_STATES } from "@coast/utils"
import AboutLessons from "../sections/AboutLessons"
import Contact from "../sections/Contact"
import FeatureList from "../sections/FeatureList"
import LocationContent from "../sections/LocationContent"
import MeetInstructors from "../sections/MeetInstructors"
import GuaranteeNews from "../sections/GuaranteeNews"
import Safety from "../sections/Safety"
import Layout from "./product-marketing/Layout"
import Section from "./product-marketing/Section"
import Reviews from "../sections/Reviews"
import { STATE_SHORT_TO_LONG } from "../../content"
import Locations from "../sections/Locations"
import CoursesInStateCTA from "./CoursesInStateCTA"
import HowToGetLicense from "./HowToGetLicense"
import HowToGetLicenseWrapper from "./HowToGetLicenseWrapper"
import TWCContent from "./TWCContent"
import { LessonCouponBanner } from "./LessonCouponBanner"

// Temporary hack to show something specific on Houston.
const CITIES_WITH_TWC_CONTENT = ["houston"]

type Props = {
  state?: string
  city?: string
  showLocations?: boolean
  children: React.ReactNode
  doShowCourses?: boolean
  doShowHowToGetLicense?: boolean
  doShowLessonCouponsBanner?: boolean
  hasHomePickup?: boolean
  hasMeetingPoints?: boolean
}

const stateShortToLongTyped: { [key: string]: string } = STATE_SHORT_TO_LONG

const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <LazyLoad height={400} offset={200} once>
    {children}
  </LazyLoad>
)

function getPageAddress({ search, pathname }: Location) {
  const zipcode = new URLSearchParams(search).get("zipcode")

  if (zipcode) {
    return zipcode
  }

  const parts = pathname.split("/location/")

  if (parts.length === 2) {
    return `${parts[1]}, ${parts[0].toUpperCase().substring(1)}`
  }

  return ""
}

const LocationLandingContent = ({
  state,
  city,
  showLocations,
  children,
  doShowCourses = true,
  doShowHowToGetLicense = true,
  doShowLessonCouponsBanner = false,
  hasHomePickup = true,
  hasMeetingPoints = true,
}: Props) => {
  const location = useLocation()
  const instructorsHead =
    city || state
      ? `Instructors serving ${city || state?.toUpperCase()}`
      : "Meet Some of our Certified Instructors"

  return (
    <Layout>
      {doShowHowToGetLicense &&
      (!state || LICENSING_STEP_STATES.includes(state?.toLowerCase())) ? (
        <HowToGetLicenseWrapper>
          <Section
            head={
              city
                ? `How to Get Your License in ${city}`
                : "How to Get Your License"
            }
            subhead="Use this handy guide to help understand the steps required to get your license."
            id="licensing-section"
          >
            <HowToGetLicense
              usState={state}
              controlsToShow={state ? ["path"] : ["state", "path"]}
            />
          </Section>
        </HowToGetLicenseWrapper>
      ) : null}
      {doShowLessonCouponsBanner ? (
        <aside>
          <LessonCouponBanner address={getPageAddress(location)} />
        </aside>
      ) : null}
      {children}
      <Reviews />
      <FeatureList USState={state || "generic"} />
      {city && (
        <LocationContent
          city={city}
          hasFixedOrMeetingPoints={hasMeetingPoints}
          hasHomePickup={hasHomePickup}
        />
      )}
      <LazyWrapper>
        <Safety location="Here" />
      </LazyWrapper>
      {state && <AboutLessons USState={state} />}
      <GuaranteeNews
        tag={state ? stateShortToLongTyped[state.toUpperCase()] : undefined}
      />
      {state && doShowCourses ? <CoursesInStateCTA usState={state} /> : null}
      <span data-cy="instructors-lazyload" />
      <LazyWrapper>
        <MeetInstructors head={instructorsHead} location={city} state={state} />
      </LazyWrapper>
      {CITIES_WITH_TWC_CONTENT.includes(city?.toLowerCase() || "") && (
        <TWCContent />
      )}
      {showLocations && (
        <LazyWrapper>
          <Locations />
        </LazyWrapper>
      )}
      <Contact />
    </Layout>
  )
}

export default LocationLandingContent
